import { ChangeEvent, ReactNode, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import { Button, Typography } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import CloudUploadIcon from "../../icons/components/cloud-upload-icon";
import { Color } from "../../theme/color";
import CloseIcon from "../../icons/components/close-icon";
import CircleIcon from "../../icons/components/circle-icon";
import { DraftIcon } from "../../icons";
import { FileUploadConfig } from "@bonliva-crm/shared";
import { AlertDialog } from "../../feedback";
import { useDialog } from "@hooks";

const DropArea = ({
  config,
  ...props
}: {
  handleFileUpload: (f: File) => void;
  config?: FileUploadConfig;
}) => {
  const dialog = useDialog<{ exts: string }>();
  const classes = useStyles({ progress: 0 });
  const hiddenFileInput = useRef<HTMLInputElement | null>(null);

  const handleClick = () => {
    if (hiddenFileInput.current) hiddenFileInput.current.click();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    const fileUploaded = event.target.files[0];

    if (config) {
      const allow = checkAllowedTypes(fileUploaded);

      if (!allow) {
        return;
      }
    }

    props.handleFileUpload(fileUploaded);
  };

  function checkAllowedTypes(file: File) {
    if (config && !config.allowType(file.type, file.name)) {
      dialog.onOpen({ exts: config.acceptString });

      return false;
    }

    return true;
  }

  return (
    <>
      <AlertDialog
        open={dialog.open}
        onOk={dialog.onClose}
        onClose={dialog.onClose}
        title={locale.FileUploadFail}
      >
        {locale.getFileUploadError(dialog.data?.exts as string)}
      </AlertDialog>

      <div
        className={classes.uploadArea}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files[0];

          if (config) {
            const allow = checkAllowedTypes(file);

            if (!allow) {
              return;
            }
          }

          props.handleFileUpload(file);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.currentTarget.classList.add(classes.onDragOver);
          e.dataTransfer.dropEffect = "copy";
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove(classes.onDragOver);
        }}
      >
        <CloudUploadIcon color="primary" fontSize="large" />
        <span className={classes.textWrapper}>
          <Typography variant="body2">{locale.FileDragNDrop}</Typography>
          <Button
            className={classes.buttonStyle}
            variant="text"
            color="primary"
            onClick={handleClick}
          >
            {locale.ChooseFile}
          </Button>
        </span>

        <input
          accept={config?.acceptString || "*/*"}
          multiple={false}
          className={classes.hiddenInput}
          type="file"
          ref={hiddenFileInput}
          onChange={handleChange}
        />
      </div>
    </>
  );
};

const UploadProgress = (props: {
  progress: number;
  file: File | null;
  removeFile: () => void;
}) => {
  const [seconds] = useState(2);

  const classes = useStyles({
    progress: props.progress
  });

  if (!props.file) return null;

  return (
    <div className={classes.fileWrapperProgress}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          width: "100%"
        }}
      >
        <DraftIcon fontSize="large" />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            width: "90%"
          }}
        >
          <div className={classes.topFlexRow}>
            <Typography variant="body2" className={classes.fileName}>
              {props.file.name}
            </Typography>
            <CloseIcon
              style={{ cursor: "pointer", position: "absolute", top: 10, right: 10 }}
              onClick={props.removeFile}
            />
          </div>

          <Typography variant="body2" className={classes.fileInfoText}>
            {props.file.size} bytes{" "}
            <CircleIcon
              htmlColor={Color.DetailsInput}
              style={{ width: "2px", margin: 4 }}
            />
            {seconds} sekunder kvar
          </Typography>
        </div>
      </div>

      <div className={classes.progressBarWrapper}>
        <Typography variant="body2">{props.progress}%</Typography>
        <div className={classes.progressBar}>
          <div
            className={classes.progressBarFill}
            style={{ width: `${props.progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const UploadCompleted = (props: {
  progress: number;
  file: File | null;
  removeFile: () => void;
}) => {
  const classes = useStyles({
    progress: props.progress
  });

  if (!props.file) return null;

  return (
    <div className={classes.fileWrapper}>
      <DraftIcon fontSize="large" />
      <div className={classes.width100}>
        <div className={classes.topFlexRow}>
          <Typography variant="body2" className={classes.fileName}>
            {props.file.name}
          </Typography>
        </div>
        <CloseIcon
          style={{ cursor: "pointer", position: "absolute", right: 10, top: 10 }}
          onClick={props.removeFile}
        />

        <Typography variant="body2" className={classes.fileInfoText}>
          {props.file.size} bytes
        </Typography>
      </div>
    </div>
  );
};

const File = (props: {
  file: File | null;
  removeFile: () => void;
  progress?: number;
}) => {
  const [_progress, _setProgress] = useState(0);

  const progress = props.progress || _progress;

  const uploadProgress = async () => {
    for (let i = 0; i <= 100; i += 10) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      _setProgress(i);
    }
  };

  useEffect(() => {
    uploadProgress();
  }, []);

  return progress === 100 ? (
    <UploadCompleted
      progress={progress}
      file={props.file}
      removeFile={props.removeFile}
    />
  ) : (
    <UploadProgress progress={progress} file={props.file} removeFile={props.removeFile} />
  );
};

export const FileUpload = (props: {
  onFileUpload: (f: File) => void;
  onFileRemove?: (f?: File) => void;
  children?: ReactNode;
  multipleUploads?: boolean;
  config?: FileUploadConfig;
}) => {
  const [progress, setProgress] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [config, setConfig] = useState(new FileUploadConfig());

  useEffect(() => {
    if (props.config) {
      setConfig(props.config);
    }
  }, [props.config]);

  const handleFileUpload = async (f: File) => {
    setFiles([...files, f]);

    // Mimick file upload progress
    for (let i = 0; i <= 100; i += 10) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      setProgress(i);
    }
    props.onFileUpload(f);
  };

  const removeFile = (index: number) => {
    setProgress(0);
    setFiles((prev) => prev.slice(0, index).concat(prev.slice(index + 1)));

    if (props.onFileRemove) {
      props.onFileRemove(files[index]);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {props.children}
      <Typography variant="h6" style={{ marginTop: 16 }}>
        {locale.FileUpload}
      </Typography>
      {!props.multipleUploads &&
        (progress > 0 ? (
          <File file={files[0]} removeFile={() => removeFile(0)} progress={progress} />
        ) : (
          <DropArea handleFileUpload={handleFileUpload} config={config} />
        ))}
      {props.multipleUploads && (
        <>
          <DropArea handleFileUpload={handleFileUpload} config={config} />
          {files.map((f, i) => (
            <File key={i} file={f} removeFile={() => removeFile(i)} />
          ))}
        </>
      )}
    </div>
  );
};

const useStyles = makeStyles({
  width100: {
    width: "100%"
  },
  uploadArea: {
    width: "100%",
    marginBottom: 16,
    border: `1px dashed ${Color.DarkGray}`,
    borderRadius: 4,
    height: "10rem",
    padding: 16,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column"
  },
  hiddenInput: {
    display: "none",
    width: "100%",
    height: "100%"
  },
  textWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center"
  },
  buttonStyle: {
    boxShadow: "none",
    textDecoration: "underline",
    padding: 0,
    marginLeft: -8,
    "&:hover": {
      border: "none",
      textDecoration: "underline",
      filter: "brightness(1.5)",
      backgroundColor: "transparent",
      "@media (hover: none)": {
        backgroundColor: "transparent"
      }
    }
  },
  onDragOver: {
    backgroundColor: `rgba(255, 134, 123, 0.15)`
  },

  fileWrapperProgress: {
    backgroundColor: `rgba(255, 134, 123, 0.2)`,
    padding: "1rem",
    borderRadius: "4px",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    height: "6rem",
    width: "22rem",
    position: "relative"
  },
  fileWrapper: {
    backgroundColor: Color.OffWhite,
    padding: "1rem",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    height: "4rem",
    width: "22rem",
    border: `1px solid ${Color.Gray}`,
    position: "relative"
  },
  topFlexRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "75%"
  },
  fileInfoText: {
    display: "flex",
    flexDirection: "row",
    color: Color.DetailsInput,
    fontSize: 10,
    alignItems: "center",
    height: "1rem"
  },
  progressBarWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-end"
  },
  progressBar: {
    width: "100%",
    height: 4,
    borderRadius: 4,
    backgroundColor: Color.DarkGray
  },
  "@keyframes progress": {
    "0%": {
      width: 0
    },
    "100%": {
      width: "100%"
    }
  },
  progressBarFill: {
    height: "100%",
    borderRadius: 4,
    backgroundColor: Color.PrimaryMain,
    animation: "$progress 1s linear"
  },
  fileName: {
    width: "75%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  textElipsis: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "15rem"
  }
});
